import { RefObject, useCallback } from 'react'
import clsx from 'clsx'
import { HugeiconsIcon, HugeiconsIconProps } from '@hugeicons/react'
import { Loading02Icon } from '@hugeicons/core-stroke-rounded'

export type Props = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  Icon?: HugeiconsIconProps['icon']
  variant?: 'outline' | 'default'
  size?: 'regular' | 'small'
  loading?: boolean
  onEnter?: (event: React.KeyboardEvent<HTMLButtonElement>) => void
  ref?: RefObject<HTMLButtonElement | null>
}

export default function Button({
  children,
  Icon,
  className,
  disabled,
  variant = 'default',
  size = 'regular',
  loading,
  onEnter,
  onKeyDown,
  ref,
  ...rest
}: Props) {
  const onKeyDownProxy = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key === 'Enter') return onEnter?.(event)

      onKeyDown?.(event)
    },
    [onKeyDown, onEnter]
  )

  const iconOnly = !children

  return (
    <button
      className={clsx(
        'relative flex flex-row items-center rounded-small cursor-pointer',
        variant === 'default' &&
          'border-2 border-primary bg-primary text-white hover:border-primary-dark hover:bg-primary-dark disabled:border-primary-light disabled:bg-primary-light disabled:hover:border-primary-light disabled:hover:bg-primary-light focus:outline-secondary',
        variant === 'outline' &&
          'border-2 border-primary bg-transparent text-primary hover:bg-primary-bright disabled:border-primary-light disabled:hover:border-primary-light disabled:hover:bg-transparent focus:outline-secondary',
        'disabled:cursor-not-allowed',
        size === 'regular' && 'gap-1.5 text-regular',
        size === 'regular' && !iconOnly && 'px-3 py-2',
        size === 'regular' && iconOnly && 'px-2 py-2',
        size === 'small' && 'gap-1 px-2 py-2 text-small',
        className
      )}
      ref={ref}
      disabled={disabled || loading}
      tabIndex={0}
      onKeyDown={onKeyDownProxy}
      type="button"
      {...rest}
    >
      {loading && (
        <div className="flex w-full flex-row items-center justify-center leading-[inherit]">
          <HugeiconsIcon icon={Loading02Icon} className="size-4 animate-spin" />
        </div>
      )}
      {!loading && (
        <>
          {Icon && (
            <HugeiconsIcon
              icon={Icon}
              className={clsx(
                size === 'regular' && 'size-4',
                size === 'small' && 'size-3'
              )}
            />
          )}
          {children && <span className="text-inherit flex-1">{children}</span>}
        </>
      )}
    </button>
  )
}
